import {
    Button,
    ButtonProps,
    Link,
    makeStyles,
    MenuButtonProps,
    mergeClasses,
    shorthands,
    SplitButton,
    splitButtonClassNames,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import {
    bundleIcon,
    Dismiss12Regular,
    PanelLeftContract28Filled,
    PanelLeftContract28Regular,
    PanelLeftExpand28Filled,
    PanelLeftExpand28Regular,
    ReceiptSparkles16Regular,
} from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router';
import { ITaxReturnTab } from '../../contexts/userSettingsContext';
import { useUserSettings } from '../../contexts/useUserSettings';
import { useFunctionalAccess } from '../../hooks/useFunctionalAccess';
import { useTaxReturnCalculationState } from '../../hooks/useTaxReturnCalculationState';
import { CalculationExecutionStatusCode } from '../../model';
import { customPalette } from '../../theme';
import { CommandType } from '../../utils/EventGrid.constants';
import { getRecentlyOpenedTabsWithStatus } from '../../utils/ReportUtils';
import { SIDEMENU_CLOSED_SIZE, SIDEMENU_SIZE } from '../../utils/Style.constants';
import { VIEW_REPORT_URL } from '../../utils/Urls.constant';
import ReportListPanelStatusBar from './ReportListPanelStatusBar';

const PanelLeftContractBundle = bundleIcon(PanelLeftContract28Filled, PanelLeftContract28Regular);
const PanelRightExpandBundle = bundleIcon(PanelLeftExpand28Filled, PanelLeftExpand28Regular);

const linkTextMaxWidthInPixels = 124;

const useStyles = makeStyles({
    root: {
        display: 'flex',
        height: '100%',
        maxWidth: '202px',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'var(--background-tabs, #F4F5FD)',
        boxShadow:
            '3px 3.200000047683716px 7.199999809265137px 0px rgba(0, 0, 0, 0.02), 1px 0.6000000238418579px 0px 0px rgba(0, 0, 0, 0.02)',
        transitionProperty: 'all',
        transitionDuration: '500ms',
        transitionTimingFunction: 'cubic-bezier(0, 0.55, 0.45, 1)',
    },
    rootExpanded: {
        width: SIDEMENU_SIZE,
        paddingTop: '32px',
        paddingRight: '10px',
        paddingBottom: '16px',
        paddingLeft: '10px',
    },
    rootCollapsed: {
        width: SIDEMENU_CLOSED_SIZE,
        paddingTop: '0',
        paddingRight: '0',
        paddingBottom: '0',
        paddingLeft: '0',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        width: '100%',
        flexGrow: 1,
        minHeight: 0,
    },
    footer: {
        width: '100%',
        alignSelf: 'end',
        maxHeight: 'inherit',
        display: 'flex',
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        borderTopWidth: '0.75px',
        borderTopColor: customPalette.neutralTertiaryAlt,
        borderTopStyle: 'solid',
    },
    footerExpanded: {
        height: '48px',
        alignItems: 'flex-end',
    },
    footerCollapsed: {
        height: '100%',
        alignItems: 'center',
        borderTopStyle: 'none',
    },
    clearLinkWrapper: {
        textAlign: 'right',
        width: '100%',
        fontSize: '12px',
        position: 'relative',
        '::before': {
            content: '""',
            display: 'block',
            width: '75%',
            height: '1px',
            backgroundColor: customPalette.neutralTertiaryAlt,
            left: '0px',
            top: '50%',
            position: 'absolute',
        },
    },
    clearLink: {
        position: 'relative',
        fontSize: '12px',
    },
    viewAllButton: {
        width: '100%',
        height: '32px',
        border: `2px solid ${customPalette.darkGreen}`,
        backgroundColor: customPalette.lightGreen,

        color: customPalette.darkGreen,
        marginBottom: '0px',
        ':hover': {
            color: customPalette.darkGreen,
            borderTopColor: customPalette.neutralQuaternaryAlt,
            borderRightColor: customPalette.neutralQuaternaryAlt,
            borderBottomColor: customPalette.neutralQuaternaryAlt,
            borderLeftColor: customPalette.neutralQuaternaryAlt,
            backgroundColor: customPalette.lightGreen,
        },
    },
    viewAllText: {
        fontSize: '14px',
        fontWeight: 500,
        letterSpacing: '-0.13px',
        textAlign: 'left',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: `${linkTextMaxWidthInPixels}px`,
    },
    viewAllIcon: {
        color: customPalette.darkGreen,
    },
    recentItem: {
        width: '100%',
    },
    splitButtonWrapper: {
        display: 'block',
        width: '100%',
    },
    splitButton: {
        width: '100%',
        minWidth: '0',
        // Primary hovered: both halves share the primary hover color (statusCoolMidGrey / rgb(245, 245, 245)).
        [`& .${splitButtonClassNames.primaryActionButton}:hover`]: {
            backgroundColor: customPalette.statusCoolMidGrey,
            color: customPalette.themePrimary,
            ...shorthands.borderColor(customPalette.themePrimary),
        },
        [`& .${splitButtonClassNames.primaryActionButton}:hover ~ .${splitButtonClassNames.menuButton}`]: {
            backgroundColor: customPalette.statusCoolMidGrey,
            color: customPalette.themePrimary,
            ...shorthands.borderColor(customPalette.themePrimary),
        },
        // Menu hovered: primary keeps primary hover; menu uses darker gray (greyD3D2D1 / rgb(211, 210, 209)).
        [`&:has(.${splitButtonClassNames.menuButton}:hover) .${splitButtonClassNames.primaryActionButton}`]: {
            backgroundColor: customPalette.statusCoolMidGrey,
            color: customPalette.themePrimary,
            ...shorthands.borderColor(customPalette.themePrimary),
        },
        [`& .${splitButtonClassNames.menuButton}:hover`]: {
            backgroundColor: customPalette.greyD3D2D1,
            color: customPalette.themePrimary,
            ...shorthands.borderColor(customPalette.themePrimary),
        },
    },
    recentItemButton: {
        width: '100%',
        minWidth: '0',
        height: '32px',
        flexGrow: 1,
        paddingTop: '9px',
        paddingBottom: '9px',
        paddingLeft: '9px',
        // trick the report name text overflow cutoff a little further to the right on the button
        paddingRight: '0px',
        backgroundColor: tokens.colorNeutralBackground1,
        // Keep adjoining edge squared so chrome styles do not undo SplitButton join.
        borderTopRightRadius: '0',
        borderBottomRightRadius: '0',
        borderTopLeftRadius: '4px',
        borderBottomLeftRadius: '4px',
        borderRight: 'none',
        justifyContent: 'flex-start',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '-0.13px',
        overflowX: 'hidden',
        '> span': {
            overflowX: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: `${linkTextMaxWidthInPixels}px`,
            minWidth: '77px',
            paddingTop: '0',
            display: 'block',
        },
    },
    removeButton: {
        // Keep adjoining edge squared so chrome styles do not undo SplitButton join.
        borderTopLeftRadius: '0',
        borderBottomLeftRadius: '0',
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
        borderBottomColor: 'revert-rule',
        borderRightColor: 'revert-rule',
        borderTopColor: 'revert-rule',
        backgroundColor: tokens.colorNeutralBackground1,
        flexShrink: 0,
    },
});

export interface IRecentlyViewedReportItemProps {
    returnKey: number;
    returnName: string;
    url: string;
    statusCode?: CalculationExecutionStatusCode;
    commandType?: CommandType;
    onOpen: (url: string) => void;
}

export const RecentlyViewedReportItem: React.FC<IRecentlyViewedReportItemProps> = (props) => {
    const { t } = useTranslation();
    const { removeReturn } = useUserSettings();
    const { functionalAccess } = useFunctionalAccess();
    const styles = useStyles();
    const canViewReturns = Boolean(functionalAccess?.viewTaxReturnsEnabled);
    const tooltipContent = canViewReturns ? props.returnName : t('noFunctionalAccess').toString();
    const removeLabel = t('removeReturnButtonLabel').toString();

    return (
        <div className={styles.recentItem}>
            <Tooltip content={tooltipContent} relationship='description' positioning='after' appearance='inverted' withArrow>
                <div>
                    <span className={styles.splitButtonWrapper}>
                        <SplitButton
                            className={styles.splitButton}
                            menuIcon={<Dismiss12Regular />}
                            primaryActionButton={
                                {
                                    as: 'a',
                                    href: props.url,
                                    'aria-label': `recently viewed return: ${props.returnName}`,
                                    onClick: (ev: React.MouseEvent<HTMLAnchorElement>) => {
                                        ev.preventDefault();
                                        props.onOpen(props.url);
                                    },
                                    className: styles.recentItemButton,
                                    disabled: !canViewReturns,
                                    'data-analytics-button-name': 'open_recent_return',
                                } as ButtonProps
                            }
                            menuButton={
                                {
                                    'aria-label': removeLabel,
                                    'data-testid': removeLabel,
                                    appearance: 'subtle',
                                    className: styles.removeButton,
                                    onClick: (ev: React.MouseEvent<HTMLButtonElement>) => {
                                        ev.stopPropagation();
                                        ev.preventDefault();
                                        removeReturn(props.returnKey);
                                    },
                                } as MenuButtonProps
                            }
                        >
                            <span>{props.returnName}</span>
                        </SplitButton>
                    </span>
                    <ReportListPanelStatusBar commandType={props.commandType} statusCode={props.statusCode} />
                </div>
            </Tooltip>
        </div>
    );
};

const ReportListPanel: React.FC = () => {
    const [isExpanded, setIsExpanded] = useState(true);
    const navigate: NavigateFunction = useNavigate();
    const { mostRecentlyOpenedTabs, clearReturnTabs, defaultsToFormsByReturnKey } = useUserSettings();
    const { taxReturnCalculationStatusList, clearAllCompletedReports } = useTaxReturnCalculationState();
    const { t } = useTranslation();
    const styles = useStyles();

    const mostRecentlyOpenedTabsWithStatus: ITaxReturnTab[] = getRecentlyOpenedTabsWithStatus(
        taxReturnCalculationStatusList || [],
        mostRecentlyOpenedTabs
    );
    const hasMostRecentlyOpenedReturns = mostRecentlyOpenedTabsWithStatus.length > 0;

    // If we're already somewhere under this link's URL (e.g. a specific form page's GUID, or a
    // comments sub-route), treat it as already-there and don't navigate - that would otherwise
    // reset the URL back to the link's generic target and lose the more specific page we're on.
    // Restricted to /forms targets: a bare report URL has no "under it" pages worth preserving,
    // and treating it as a prefix would turn clicking a report-having return while on its Forms
    // page into a silent no-op instead of navigating back to the report.
    const handleNavigate = (url: string) => {
        const { pathname } = window.location;
        const isFormsTarget = url.endsWith('/forms');
        const isAlreadyOnTarget = url !== '/' && (pathname === url || (isFormsTarget && pathname.startsWith(`${url}/`)));
        if (!isAlreadyOnTarget) {
            navigate(url);
        }
    };

    const handleClearReturnsClick = () => {
        clearReturnTabs();
        clearAllCompletedReports();
    };

    if (!hasMostRecentlyOpenedReturns) {
        return null;
    }

    const toggleLabel = t('recentlyReviewedReportsPanelToggle').toString();
    const tooltipText = isExpanded ? 'collapsePanel' : 'expandPanelSize';
    return (
        <nav
            aria-label={t('recentlyReviewedReports').toString()}
            className={mergeClasses(styles.root, isExpanded ? styles.rootExpanded : styles.rootCollapsed)}
        >
            {isExpanded && (
                <div className={styles.content}>
                    <div className={styles.clearLinkWrapper}>
                        <Link
                            as='button'
                            className={styles.clearLink}
                            onClick={handleClearReturnsClick}
                            aria-label={t('clearMostRecentReturnsButtonLabel').toString()}
                        >
                            {t('clearMostRecentReturnsButtonText').toString()}
                        </Link>
                    </div>
                    <div>
                        <Button
                            as='a'
                            href='/'
                            icon={<ReceiptSparkles16Regular className={styles.viewAllIcon} />}
                            aria-label={t('calculations').toString()}
                            onClick={(ev) => {
                                ev.preventDefault();
                                handleNavigate('/');
                            }}
                            className={styles.viewAllButton}
                        >
                            <span className={styles.viewAllText}>{t('calculations')}</span>
                        </Button>
                    </div>

                    {mostRecentlyOpenedTabsWithStatus.map((tab) => {
                        // If we already know (from viewing it earlier this session) that this return has no usable
                        // report and defaults to Forms, link straight to /forms instead of the bare report URL - so
                        // re-selecting it while already there is a true no-op instead of landing on the report URL and
                        // immediately bouncing back to Forms.
                        const targetUrl = defaultsToFormsByReturnKey[tab.returnKey]
                            ? `${VIEW_REPORT_URL}${tab.returnKey}/forms`
                            : `${VIEW_REPORT_URL}${tab.returnKey}`;
                        return (
                            <RecentlyViewedReportItem
                                key={tab.returnKey}
                                returnKey={tab.returnKey}
                                returnName={tab.returnName ?? ''}
                                url={targetUrl}
                                statusCode={tab.statusCode}
                                commandType={tab.commandType}
                                onOpen={handleNavigate}
                            />
                        );
                    })}
                </div>
            )}
            <div
                className={mergeClasses(styles.footer, isExpanded ? styles.footerExpanded : styles.footerCollapsed)}
                id='reportListPanelToggleWidthButtonWrapper'
            >
                <Tooltip content={t(tooltipText).toString()} relationship='description' appearance='inverted' withArrow positioning='after'>
                    <Button
                        appearance='subtle'
                        icon={isExpanded ? <PanelLeftContractBundle /> : <PanelRightExpandBundle />}
                        aria-label={toggleLabel}
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-expanded={isExpanded}
                    />
                </Tooltip>
            </div>
        </nav>
    );
};

export default ReportListPanel;
